import { combineReducers } from 'redux';

import { Reducer } from 'Lib/reduxType';

import {
  getCommonResetAction,
  STATE_ERROR,
  STATE_INITIAL,
  STATE_LOADING,
  STATE_SUCCESS,
} from 'Store/commonActions';
import {
  REMOVE_EMAIL_ERROR,
  REMOVE_EMAIL_SUCCESS,
  RESET_GOOGLE_TOKEN,
  RESET_SEND_RECOVERY_EMAIL_STATE,
  RESET_VALIDATE_EMAIL_OTP_STATE,
  SEND_EMAIL,
  SEND_EMAIL_CLEAR_ERROR_RESPONSE,
  SEND_EMAIL_ERROR,
  SEND_EMAIL_SUCCESS,
  SEND_RECOVERY_EMAIL,
  SEND_RECOVERY_EMAIL_ERROR,
  SEND_RECOVERY_EMAIL_SUCCESS,
  SET_EMAIL_AUTH_STATE,
  SET_EMAIL_OTP_LENGTH,
  SET_MARKETING_EMAIL_OPTIONS,
  SET_MASKED_EMAIL,
  SET_SECOND_FACTOR_THIRD_PARTY,
  VALIDATE_EMAIL_OTP_ERROR,
  VALIDATE_EMAIL_OTP_SUCCESS,
  VALIDATE_GOOGLE_TOKEN_ERROR,
  VALIDATE_GOOGLE_TOKEN_SUCCESS,
} from 'Store/email/constants';
import initialState from 'Store/email/initialState';
import type { EmailState } from 'Store/email/types';
import {
  LOGIN_START,
  SUBMIT_EMAIL,
  SUBMIT_EMAIL_OTP,
  SUBMIT_GOOGLE_TOKEN,
} from 'Store/mfa/constants';

const sendEmailStateReducer: Reducer<EmailState['sendEmailState']> = (
  state = initialState.sendEmailState,
  action
) => {
  switch (action.type) {
    case SEND_EMAIL:
    case SUBMIT_EMAIL:
      return STATE_LOADING;
    case SEND_EMAIL_SUCCESS:
      return STATE_SUCCESS;
    case SEND_EMAIL_ERROR:
      return STATE_ERROR;
    case SEND_EMAIL_CLEAR_ERROR_RESPONSE:
      return STATE_INITIAL;
    case LOGIN_START:
    case getCommonResetAction(action.type):
      return initialState.sendEmailState;
    default:
      return state;
  }
};

const sendEmailErrorReducer: Reducer<EmailState['sendEmailError']> = (
  state = initialState.sendEmailError,
  action
) => {
  switch (action.type) {
    case SEND_EMAIL_ERROR:
      return action.payload.error;
    case SEND_EMAIL:
    case SUBMIT_EMAIL:
    case SEND_EMAIL_SUCCESS:
    case SEND_EMAIL_CLEAR_ERROR_RESPONSE:
    case LOGIN_START:
    case getCommonResetAction(action.type):
      return initialState.sendEmailError;
    default:
      return state;
  }
};

const removeEmailStateReducer: Reducer<EmailState['removeEmailState']> = (
  state = initialState.removeEmailState,
  action
) => {
  switch (action.type) {
    case REMOVE_EMAIL_SUCCESS:
      return STATE_SUCCESS;
    case REMOVE_EMAIL_ERROR:
      return STATE_ERROR;
    case LOGIN_START:
    case getCommonResetAction(action.type):
      return initialState.removeEmailState;
    default:
      return state;
  }
};

const sendRecoveryEmailStateReducer: Reducer<EmailState['sendRecoveryEmailState']> = (
  state = initialState.sendRecoveryEmailState,
  action
) => {
  switch (action.type) {
    case SEND_RECOVERY_EMAIL:
      return STATE_LOADING;
    case SEND_RECOVERY_EMAIL_SUCCESS:
      return STATE_SUCCESS;
    case SEND_RECOVERY_EMAIL_ERROR:
      return STATE_ERROR;
    case RESET_SEND_RECOVERY_EMAIL_STATE:
      return STATE_INITIAL;
    case LOGIN_START:
    case getCommonResetAction(action.type):
      return initialState.sendRecoveryEmailState;
    default:
      return state;
  }
};

const emailOtpStateReducer: Reducer<EmailState['emailOtpState']> = (
  state = initialState.emailOtpState,
  action
) => {
  switch (action.type) {
    case SUBMIT_EMAIL_OTP:
      return STATE_LOADING;
    case VALIDATE_EMAIL_OTP_SUCCESS:
      return STATE_SUCCESS;
    case VALIDATE_EMAIL_OTP_ERROR:
      return STATE_ERROR;
    case SET_EMAIL_AUTH_STATE:
      return STATE_INITIAL;
    case RESET_VALIDATE_EMAIL_OTP_STATE:
      return STATE_INITIAL;
    case LOGIN_START:
    case getCommonResetAction(action.type):
      return initialState.emailOtpState;
    default:
      return state;
  }
};

const emailOtpErrorReducer: Reducer<EmailState['emailOtpError']> = (
  state = initialState.emailOtpError,
  action
) => {
  switch (action.type) {
    case VALIDATE_EMAIL_OTP_ERROR:
      return action.payload.error;
    case SUBMIT_EMAIL_OTP:
    case VALIDATE_EMAIL_OTP_SUCCESS:
    case SET_EMAIL_AUTH_STATE:
    case RESET_VALIDATE_EMAIL_OTP_STATE:
    case LOGIN_START:
    case getCommonResetAction(action.type):
      return initialState.emailOtpError;
    default:
      return state;
  }
};

const googleTokenStateReducer: Reducer<EmailState['googleTokenState']> = (
  state = initialState.googleTokenState,
  action
) => {
  switch (action.type) {
    case RESET_GOOGLE_TOKEN:
      return initialState.googleTokenState;
    case SUBMIT_GOOGLE_TOKEN:
      return STATE_LOADING;
    case VALIDATE_GOOGLE_TOKEN_SUCCESS:
      return STATE_SUCCESS;
    case VALIDATE_GOOGLE_TOKEN_ERROR:
      return STATE_ERROR;
    case LOGIN_START:
    case getCommonResetAction(action.type):
      return initialState.googleTokenState;
    default:
      return state;
  }
};

const googleTokenErrorReducer: Reducer<EmailState['googleTokenError']> = (
  state = initialState.googleTokenError,
  action
) => {
  switch (action.type) {
    case VALIDATE_GOOGLE_TOKEN_ERROR:
      return action.payload.error;
    case SUBMIT_GOOGLE_TOKEN:
    case VALIDATE_GOOGLE_TOKEN_SUCCESS:
    case LOGIN_START:
    case getCommonResetAction(action.type):
      return initialState.googleTokenError;
    default:
      return state;
  }
};

const emailAuthStateReducer: Reducer<EmailState['emailAuthState']> = (
  state = initialState.emailAuthState,
  action
) => {
  switch (action.type) {
    case SET_EMAIL_AUTH_STATE:
      return action.payload.emailAuthState;
    case LOGIN_START:
    case getCommonResetAction(action.type):
      return initialState.emailAuthState;
    default:
      return state;
  }
};

const maskedEmailReducer: Reducer<EmailState['maskedEmail']> = (
  state = initialState.maskedEmail,
  action
) => {
  switch (action.type) {
    case SET_MASKED_EMAIL:
      return action.payload.maskedEmail;
    case LOGIN_START:
    case getCommonResetAction(action.type):
      return initialState.maskedEmail;
    default:
      return state;
  }
};

const secondFactorThirdPartyReducer: Reducer<EmailState['secondFactorThirdParty']> = (
  state = initialState.secondFactorThirdParty,
  action
) => {
  switch (action.type) {
    case SET_SECOND_FACTOR_THIRD_PARTY:
      return action.payload.secondFactorThirdParty;
    case LOGIN_START:
    case getCommonResetAction(action.type):
      return initialState.secondFactorThirdParty;
    default:
      return state;
  }
};

const otpLengthReducer: Reducer<EmailState['otpLength']> = (
  state = initialState.otpLength,
  action
) => {
  switch (action.type) {
    case SET_EMAIL_OTP_LENGTH:
      return action.payload.otpLength;
    case LOGIN_START:
    case getCommonResetAction(action.type):
      return initialState.otpLength;
    default:
      return state;
  }
};

const showMarketingOptInReducer: Reducer<EmailState['showMarketingOptIn']> = (
  state = initialState.showMarketingOptIn,
  action
) => {
  switch (action.type) {
    case SET_MARKETING_EMAIL_OPTIONS:
      return action.payload.showMarketingOptIn;
    case LOGIN_START:
    case getCommonResetAction(action.type):
      return initialState.showMarketingOptIn;
    default:
      return state;
  }
};

const showStrictOptInReducer: Reducer<EmailState['showStrictOptIn']> = (
  state = initialState.showStrictOptIn,
  action
) => {
  switch (action.type) {
    case SET_MARKETING_EMAIL_OPTIONS:
      return action.payload.showStrictOptIn;
    case LOGIN_START:
    case getCommonResetAction(action.type):
      return initialState.showStrictOptIn;
    default:
      return state;
  }
};

const checkedByDefaultReducer: Reducer<EmailState['checkedByDefault']> = (
  state = initialState.checkedByDefault,
  action
) => {
  switch (action.type) {
    case SET_MARKETING_EMAIL_OPTIONS:
      return action.payload.checkedByDefault;
    case LOGIN_START:
    case getCommonResetAction(action.type):
      return initialState.checkedByDefault;
    default:
      return state;
  }
};

export const reducer = combineReducers({
  sendEmailState: sendEmailStateReducer,
  sendEmailError: sendEmailErrorReducer,
  removeEmailState: removeEmailStateReducer,
  sendRecoveryEmailState: sendRecoveryEmailStateReducer,
  emailOtpState: emailOtpStateReducer,
  emailOtpError: emailOtpErrorReducer,
  googleTokenState: googleTokenStateReducer,
  googleTokenError: googleTokenErrorReducer,
  emailAuthState: emailAuthStateReducer,
  maskedEmail: maskedEmailReducer,
  otpLength: otpLengthReducer,
  showMarketingOptIn: showMarketingOptInReducer,
  showStrictOptIn: showStrictOptInReducer,
  checkedByDefault: checkedByDefaultReducer,
  secondFactorThirdParty: secondFactorThirdPartyReducer,
});
